export const ELECTION_MAP_STANDARD_V1_KEY = "electionmap-v1";

export const ELECTION_MAP_STANDARD_V1 = {
  key: ELECTION_MAP_STANDARD_V1_KEY,
  baseMapVariant: "voyager",
  baseTileUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png",
  labelsTileUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png",
  paneZIndexes: {
    base: 200,
    mask: 300,
    precinct: 410,
    labels: 520,
    markers: 610,
  },
  outsideMaskFillColor: "#EEF2F6",
  outsideMaskOpacity: 0.72,
  normalFillOpacity: 0.78,
  clearFocusFillColor: "#f8fafc",
  clearFocusFillOpacity: 0.03,
  normalBorderColor: "#1f2937",
  noDataBorderColor: "#64748b",
  hoverBorderColor: "#111827",
  selectedBorderColor: "#0f172a",
  normalBorderWeight: 1.2,
  hoverBorderWeight: 3.6,
  selectedBorderWeight: 4.6,
};

export const MAP_TILE_VARIANTS = {
  voyager: {
    baseTileUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png",
    labelsTileUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png",
  },
  positron: {
    baseTileUrl: "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png",
    labelsTileUrl: "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png",
  },
};

export const WORLD_MASK_RING = [
  [85, -180],
  [85, 180],
  [-85, 180],
  [-85, -180],
];

function normalizeStandardKey(value) {
  return String(value || "").trim().toLowerCase();
}

export function isElectionMapStandardV1(value) {
  const key = normalizeStandardKey(value);
  return key === ELECTION_MAP_STANDARD_V1_KEY || key === "v1";
}

export function getMapTileUrlsForVariant(baseMapVariant) {
  const key = String(baseMapVariant || "").trim().toLowerCase();
  return MAP_TILE_VARIANTS[key] || null;
}

function visitGeoJsonCoordinatePairs(value, callback) {
  if (!Array.isArray(value)) return;
  if (
    value.length >= 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  ) {
    callback(Number(value[0]), Number(value[1]));
    return;
  }

  for (const child of value) {
    visitGeoJsonCoordinatePairs(child, callback);
  }
}

export function getGeoJsonCoordinateBounds(geoJson) {
  const features = Array.isArray(geoJson?.features) ? geoJson.features : [];
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;

  const updateBounds = (lng, lat) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    minLat = Math.min(minLat, lat);
    minLng = Math.min(minLng, lng);
    maxLat = Math.max(maxLat, lat);
    maxLng = Math.max(maxLng, lng);
  };

  for (const feature of features) {
    visitGeoJsonCoordinatePairs(feature?.geometry?.coordinates, updateBounds);
  }

  if (![minLat, minLng, maxLat, maxLng].every(Number.isFinite)) return null;

  const latPad = Math.max((maxLat - minLat) * 0.035, 0.0005);
  const lngPad = Math.max((maxLng - minLng) * 0.035, 0.0005);

  return {
    minLat: Math.max(-85, minLat - latPad),
    minLng: Math.max(-180, minLng - lngPad),
    maxLat: Math.min(85, maxLat + latPad),
    maxLng: Math.min(180, maxLng + lngPad),
  };
}

export function getBoundsMaskPositions(geoJson) {
  const bounds = getGeoJsonCoordinateBounds(geoJson);
  if (!bounds) return null;

  const boundsHole = [
    [bounds.maxLat, bounds.minLng],
    [bounds.maxLat, bounds.maxLng],
    [bounds.minLat, bounds.maxLng],
    [bounds.minLat, bounds.minLng],
  ];

  return [WORLD_MASK_RING, boundsHole];
}

export function ringToLatLngRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const latLngRing = [];

  for (const coordinate of ring) {
    if (!Array.isArray(coordinate) || coordinate.length < 2) continue;
    const lng = Number(coordinate[0]);
    const lat = Number(coordinate[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    latLngRing.push([lat, lng]);
  }

  return latLngRing.length >= 3 ? latLngRing : null;
}

function addPolygonRingsAsMaskCutouts(coordinates, rings) {
  for (const ring of Array.isArray(coordinates) ? coordinates : []) {
    const latLngRing = ringToLatLngRing(ring);
    if (latLngRing) rings.push(latLngRing);
  }
}

export function getPrecinctMaskPositions(geoJson, { fallbackToBounds = false } = {}) {
  const features = Array.isArray(geoJson?.features) ? geoJson.features : [];
  const rings = [];

  for (const feature of features) {
    const geometry = feature?.geometry;
    const coordinates = geometry?.coordinates;

    if (geometry?.type === "Polygon") {
      addPolygonRingsAsMaskCutouts(coordinates, rings);
    } else if (geometry?.type === "MultiPolygon") {
      for (const polygon of Array.isArray(coordinates) ? coordinates : []) {
        addPolygonRingsAsMaskCutouts(polygon, rings);
      }
    }
  }

  if (rings.length > 0) return [WORLD_MASK_RING, ...rings];
  return fallbackToBounds ? getBoundsMaskPositions(geoJson) : null;
}

export function getOutsideAreaMaskPositions(geoJson, { geometry = "precincts", fallbackToBounds = false } = {}) {
  if (geometry === "bounds") return getBoundsMaskPositions(geoJson);
  return getPrecinctMaskPositions(geoJson, { fallbackToBounds });
}

export function getElectionMapFeatureStyle({
  fillColor,
  noDataFillColor,
  isHovered = false,
  isSelected = false,
  clearOnFocus = true,
  selectedBorderWeight = ELECTION_MAP_STANDARD_V1.selectedBorderWeight,
} = {}) {
  const isFocused = Boolean(isHovered || isSelected);
  const shouldClear = clearOnFocus && isFocused;
  const resolvedFillColor = shouldClear ? ELECTION_MAP_STANDARD_V1.clearFocusFillColor : fillColor;

  if (isSelected) {
    return {
      color: ELECTION_MAP_STANDARD_V1.selectedBorderColor,
      weight: selectedBorderWeight,
      opacity: 1,
      fillColor: resolvedFillColor,
      fillOpacity: shouldClear
        ? ELECTION_MAP_STANDARD_V1.clearFocusFillOpacity
        : ELECTION_MAP_STANDARD_V1.normalFillOpacity,
    };
  }

  if (isHovered) {
    return {
      color: ELECTION_MAP_STANDARD_V1.hoverBorderColor,
      weight: ELECTION_MAP_STANDARD_V1.hoverBorderWeight,
      opacity: 0.98,
      fillColor: resolvedFillColor,
      fillOpacity: shouldClear
        ? ELECTION_MAP_STANDARD_V1.clearFocusFillOpacity
        : ELECTION_MAP_STANDARD_V1.normalFillOpacity,
    };
  }

  return {
    color:
      noDataFillColor && fillColor === noDataFillColor
        ? ELECTION_MAP_STANDARD_V1.noDataBorderColor
        : ELECTION_MAP_STANDARD_V1.normalBorderColor,
    weight: ELECTION_MAP_STANDARD_V1.normalBorderWeight,
    opacity: 0.78,
    fillColor: resolvedFillColor,
    fillOpacity: ELECTION_MAP_STANDARD_V1.normalFillOpacity,
  };
}
